import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { hapticFeedback } from '../../utils/hapticFeedback';

interface Place {
  id: number;
  nameMn: string;
  nameEn: string;
  category: string;
  mood: string[];
  budget: number;
  rating: number;
  views: number;
  isOpen: boolean;
  verified: string;
  tip: string;
  description: string;
  hours: string;
  contact: string;
  address: string;
  lat: number;
  lng: number;
  image: string;
  timeOfDay: string[];
  amenities: string[];
}

interface PlaceDetailModalProps {
  place: Place;
  onClose: () => void;
}

export default function PlaceDetailModal({ place, onClose }: PlaceDetailModalProps) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hapticFeedback.tap();
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleGetDirections = () => {
    hapticFeedback.select();
    const destination = `${place.lat},${place.lng}`;
    const placeName = encodeURIComponent(isEnglish ? place.nameEn : place.nameMn);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=${placeName}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    hapticFeedback.tap();
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: isEnglish ? place.nameEn : place.nameMn,
          text: place.tip,
          url: url
        });
        hapticFeedback.success();
      } catch (error) {
        // User cancelled or error occurred
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        hapticFeedback.success();
        alert(isEnglish ? 'Link copied to clipboard!' : 'Холбоос хуулагдлаа!');
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const handleSave = () => {
    hapticFeedback.select();
    // Get existing favorites from localStorage
    const favoritesStr = localStorage.getItem('favorites');
    const favorites = favoritesStr ? JSON.parse(favoritesStr) : [];
    
    // Check if already saved
    const isSaved = favorites.includes(place.id);
    
    if (isSaved) {
      // Remove from favorites
      const updated = favorites.filter((id: number) => id !== place.id);
      localStorage.setItem('favorites', JSON.stringify(updated));
      alert(isEnglish ? 'Removed from favorites' : 'Дуртайгаас хасагдлаа');
    } else {
      // Add to favorites
      favorites.push(place.id);
      localStorage.setItem('favorites', JSON.stringify(favorites));
      hapticFeedback.success();
      alert(isEnglish ? 'Added to favorites!' : 'Дуртайд нэмэгдлээ!');
    }
  };

  const handleClose = () => {
    hapticFeedback.tap();
    onClose();
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      eat: isEnglish ? 'Restaurant' : 'Ресторан',
      party: isEnglish ? 'Nightlife' : 'Шөнийн амьдрал',
      explore: isEnglish ? 'Attraction' : 'Үзвэр газар',
      romantic: isEnglish ? 'Date Spot' : 'Болзооны газар',
      unique: isEnglish ? 'Unique Place' : 'Онцгой газар',
      spa: isEnglish ? 'Spa & Massage' : 'Спа & Массаж',
      coffee: isEnglish ? 'Coffee Shop' : 'Кофе шоп',
      shopping: isEnglish ? 'Shopping' : 'Дэлгүүр'
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      eat: 'ri-restaurant-line',
      party: 'ri-music-2-line',
      explore: 'ri-compass-3-line',
      romantic: 'ri-heart-line',
      unique: 'ri-star-line',
      spa: 'ri-leaf-line',
      coffee: 'ri-cup-line',
      shopping: 'ri-shopping-bag-line'
    };
    return icons[category] || 'ri-map-pin-line';
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
      style={{ animation: 'fadeIn 300ms ease-out' }}
    >
      <div 
        className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'slideInUp 300ms ease-out' }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-300 cursor-pointer hover:scale-110"
        >
          <i className="ri-close-line text-xl text-gray-700"></i>
        </button>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Hero Image */}
          <div className="relative w-full h-72">
            <img
              src={place.image}
              alt={isEnglish ? place.nameEn : place.nameMn}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            
            {/* Category Badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <i className={`${getCategoryIcon(place.category)} text-teal-600`}></i>
              <span className="text-sm font-medium text-gray-700">{getCategoryLabel(place.category)}</span>
            </div>

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h2 className="text-3xl font-bold text-white mb-2">
                {isEnglish ? place.nameEn : place.nameMn}
              </h2>
              <div className="flex items-center gap-4 text-white/90">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <span className="font-semibold">{place.rating}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  <span>{place.views.toLocaleString()} {t('placeCard.views')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium">{'$'.repeat(place.budget)}</span>
                  <span className="text-white/60">{'$'.repeat(3 - place.budget)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Status & Verification */}
            <div className="flex items-center gap-4 mb-6">
              <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                place.isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                <i className={`${place.isOpen ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} mr-1`}></i>
                {place.isOpen ? t('placeCard.open') : t('placeCard.closed')}
              </div>
              <div className="flex items-center gap-2 text-sm text-teal-600">
                <i className="ri-verified-badge-fill"></i>
                <span>{t('placeCard.verified')} {place.verified}</span>
              </div>
            </div>

            {/* Insider Tip */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <i className="ri-lightbulb-line text-amber-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    {isEnglish ? 'Insider Tip' : 'Зөвлөгөө'}
                  </p>
                  <p className="text-amber-900 italic">"{place.tip}"</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {isEnglish ? 'About' : 'Тухай'}
              </h3>
              <p className="text-gray-600 leading-relaxed">{place.description}</p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Hours */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-time-line text-teal-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{t('placeCard.hours')}</p>
                    <p className="text-gray-900 font-medium">{place.hours}</p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-phone-line text-teal-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{t('placeCard.contact')}</p>
                    <p className="text-gray-900 font-medium">{place.contact}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 rounded-xl p-4 md:col-span-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-map-pin-line text-teal-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      {isEnglish ? 'Address' : 'Хаяг'}
                    </p>
                    <p className="text-gray-900 font-medium">{place.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {isEnglish ? 'Amenities' : 'Үйлчилгээ'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {place.amenities.map((amenity, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {amenity}
                  </span>
                ))}
              </div>
            </div>

            {/* Best Time to Visit */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {isEnglish ? 'Best Time to Visit' : 'Зочлох хамгийн тохиромжтой цаг'}
              </h3>
              <div className="flex gap-3">
                {place.timeOfDay.includes('day') && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <i className="ri-sun-line text-yellow-600"></i>
                    <span className="text-yellow-800 font-medium">
                      {isEnglish ? 'Daytime' : 'Өдөр'}
                    </span>
                  </div>
                )}
                {place.timeOfDay.includes('night') && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <i className="ri-moon-line text-indigo-600"></i>
                    <span className="text-indigo-800 font-medium">
                      {isEnglish ? 'Nighttime' : 'Шөнө'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button 
                onClick={handleGetDirections}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:shadow-lg active:scale-95"
              >
                <i className="ri-map-pin-line"></i>
                {t('placeCard.directions')}
              </button>
              <button 
                onClick={handleShare}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:shadow-md active:scale-95"
              >
                <i className="ri-share-line"></i>
                {isEnglish ? 'Share' : 'Хуваалцах'}
              </button>
              <button 
                onClick={handleSave}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:shadow-md active:scale-95"
              >
                <i className="ri-heart-line"></i>
                {isEnglish ? 'Save' : 'Хадгалах'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
